import { supabase } from '../lib/supabase';

// Fetch all visible videos for public About page
export const fetchVisiblePromoVideos = async () => {
  try {
    const { data, error } = await supabase?.from('promo_videos')?.select('*')?.eq('is_visible', true)?.order('display_order', { ascending: true });

    if (error) {
      // PGRST205 = schema cache miss — table exists but PostgREST cache is stale
      // Trigger a schema reload and return empty array gracefully
      if (error?.code === 'PGRST205') {
        console.warn('PostgREST schema cache miss for promo_videos. Cache reload triggered. Retrying once...');
        // Wait briefly for cache to reload, then retry once
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retry = await supabase?.from('promo_videos')?.select('*')?.eq('is_visible', true)?.order('display_order', { ascending: true });
        if (retry?.error) {
          console.error('Retry failed for promo_videos:', retry?.error);
          return [];
        }
        return retry?.data || [];
      }
      throw error;
    }
    return data || [];
  } catch (err) {
    console.error('fetchVisiblePromoVideos error:', err);
    return [];
  }
};

// Fetch all videos for admin (including hidden)
export const fetchAllPromoVideos = async () => {
  try {
    const { data, error } = await supabase?.from('promo_videos')?.select('*')?.order('display_order', { ascending: true });

    if (error) {
      if (error?.code === 'PGRST205') {
        console.warn('PostgREST schema cache miss for promo_videos (admin). Retrying...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retry = await supabase?.from('promo_videos')?.select('*')?.order('display_order', { ascending: true });
        if (retry?.error) {
          console.error('Retry failed for promo_videos (admin):', retry?.error);
          return [];
        }
        return retry?.data || [];
      }
      throw error;
    }
    return data || [];
  } catch (err) {
    console.error('fetchAllPromoVideos error:', err);
    return [];
  }
};

// Create a new promo video record
export const createPromoVideo = async (videoData) => {
  const { data, error } = await supabase?.from('promo_videos')?.insert({
      title: videoData?.title,
      description: videoData?.description,
      category: videoData?.category,
      video_url: videoData?.videoUrl || null,
      thumbnail_url: videoData?.thumbnailUrl || null,
      duration: videoData?.duration || null,
      is_visible: videoData?.isVisible !== undefined ? videoData?.isVisible : true,
      display_order: videoData?.displayOrder || 0,
      scheduled_at: videoData?.scheduledAt || null,
    })?.select()?.single();

  if (error) throw error;
  return data;
};

// Update a promo video
export const updatePromoVideo = async (id, updates) => {
  const payload = {};
  if (updates?.title !== undefined) payload.title = updates?.title;
  if (updates?.description !== undefined) payload.description = updates?.description;
  if (updates?.category !== undefined) payload.category = updates?.category;
  if (updates?.videoUrl !== undefined) payload.video_url = updates?.videoUrl;
  if (updates?.thumbnailUrl !== undefined) payload.thumbnail_url = updates?.thumbnailUrl;
  if (updates?.duration !== undefined) payload.duration = updates?.duration;
  if (updates?.isVisible !== undefined) payload.is_visible = updates?.isVisible;
  if (updates?.displayOrder !== undefined) payload.display_order = updates?.displayOrder;
  if (updates?.scheduledAt !== undefined) payload.scheduled_at = updates?.scheduledAt;

  const { data, error } = await supabase?.from('promo_videos')?.update(payload)?.eq('id', id)?.select()?.single();

  if (error) throw error;
  return data;
};

// Toggle visibility
export const togglePromoVideoVisibility = async (id, isVisible) => {
  const { data, error } = await supabase?.from('promo_videos')?.update({ is_visible: isVisible })?.eq('id', id)?.select()?.single();

  if (error) throw error;
  return data;
};

// Delete a promo video
export const deletePromoVideo = async (id) => {
  const { error } = await supabase?.from('promo_videos')?.delete()?.eq('id', id);

  if (error) throw error;
};

// Upload video file to storage
export const uploadPromoVideoFile = async (file, folder = 'videos') => {
  const timestamp = Date.now();
  const safeName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `${folder}/${timestamp}-${safeName}`;

  const { data, error } = await supabase?.storage?.from('promo-videos')?.upload(filePath, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data: { publicUrl } } = supabase?.storage?.from('promo-videos')?.getPublicUrl(data?.path);

  return { path: data?.path, publicUrl };
};

// Upload thumbnail image to storage
export const uploadPromoThumbnail = async (file) => {
  return uploadPromoVideoFile(file, 'thumbnails');
};

// Delete file from storage
export const deletePromoVideoFile = async (filePath) => {
  const { error } = await supabase?.storage?.from('promo-videos')?.remove([filePath]);

  if (error) throw error;
};
